"use client";

/**
 * Impersonation engine — the React shell.
 *
 * Mounted once in ClientLayout and lives for the session. Owns the impersonation stack
 * (persisted via core.ts) and exposes the universal API — `impersonate` / `exit` /
 * `enterClientImpersonation` / `setImpersonatedUserId` / `canImpersonate` — plus the
 * derived "current identity" view through context, consumed via `useImpersonation()`.
 *
 * All pure logic lives in core.ts (state transitions, derivation, headers). This file is
 * the thin orchestrator: it asks core for the next state and delegates each side-effect
 * to one focused hook —
 *   - useImpersonationTheme  : swap to / reset the firm's branding
 *   - useNavExitGuard        : unwind when the user navigates out of the active route tree
 *   - useStaleImpersonationRouteGuard : bounce staff home on Back into a route we've exited
 *
 * The context loader (`isTransitioning`) is derived here, identity-driven: it's true
 * whenever the committed route isn't yet inside the active identity's tree (a switch in
 * flight) OR the stale-route guard is bouncing us home. The dashboard layout renders a
 * spinner for it, so the experience we're leaving unmounts before any of its queries can
 * fire under the new context's headers.
 *
 * See docs/impersonation.md.
 */
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from "react";
import { usePathname, useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { useUserRoleStore } from "@/shared/store/userStore";
import { logger } from "@/shared/lib/logger";
import { ROLE_CHOICES, RoleType } from "@/shared/lib/types";
import { ROLE_REGISTRY } from "./registry";
import {
  readState,
  writeState,
  pushTarget,
  popTarget,
  refineTopUserId,
  deriveImpersonationView,
  writeEntryOrigin,
} from "./core";
import { useImpersonationTheme } from "./useImpersonationTheme";
import { useNavExitGuard } from "./useNavExitGuard";
import { useStaleImpersonationRouteGuard } from "./useStaleImpersonationRouteGuard";
import {
  DEFAULT_POLICY,
  isImpersonationAllowed,
  resolveActor,
} from "./permissions";
import type {
  ImpersonationKind,
  ImpersonationState,
  ImpersonationTarget,
  ImpersonationContextValue,
  ImpersonationProviderProps,
} from "./types";

/** Exported so the `useImpersonation` hook (separate file) can consume it. */
export const ImpersonationContext = createContext<
  ImpersonationContextValue | undefined
>(undefined);

export function ImpersonationProvider({
  children,
  policy = DEFAULT_POLICY,
  enforce = true,
  onPermissionDenied,
}: ImpersonationProviderProps): JSX.Element {
  const router = useRouter();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const userRole = useUserRoleStore((s) => s.userRole);

  // The impersonation stack (top = who we're acting as), persisted to sessionStorage.
  const [state, setState] = useState<ImpersonationState>(null);
  const persist = useCallback((next: ImpersonationState): void => {
    writeState(next);
    setState(next);
  }, []);

  const theme = useImpersonationTheme();

  // Armed once the browser is confirmed INSIDE the active layer's tree; the nav-exit
  // guard only fires when armed, and impersonate/exit disarm it before navigating.
  const armedRef = useRef(false);

  // Until the stack is hydrated from sessionStorage, `state` is transiently null even on
  // a legitimately-impersonated page — so the stale-route guard must hold off (else a
  // refresh would bounce staff to /landing). Flips true once the mount effect runs.
  const [hydrated, setHydrated] = useState(false);

  // Set on a FULL exit; the impersonated cache is dropped only once the exit navigation
  // has committed (pathname has left the impersonated tree). Doing it inline in exit()
  // instead fires synchronously while the impersonated page is still mounted — emptying
  // the cache makes its active query observers immediately refetch under now-cleared
  // headers (the favoriteCharities "'NoneType' … meta_data" bug). After the navigation
  // commits the impersonated experience has unmounted, so its queries are now INACTIVE —
  // we remove only those, which (a) can't trigger a refetch (nothing is observing them)
  // and (b) leaves the landing route's freshly-mounted queries alone (no double-fetch).
  const pendingExitClearRef = useRef(false);

  // Hydrate the stack after mount (reading sessionStorage during render mismatches SSR).
  useEffect(() => {
    setState(readState());
    armedRef.current = false;
    setHydrated(true);
  }, []);

  // Drop the impersonated cache after the exit navigation commits (see the ref above).
  // `type: "inactive"` = only queries with no live observer — i.e. the impersonated
  // experience we just unmounted — so this never refetches and never disturbs the
  // landing route's active queries.
  useEffect(() => {
    if (pendingExitClearRef.current) {
      pendingExitClearRef.current = false;
      queryClient.removeQueries({ type: "inactive" });
    }
  }, [pathname, queryClient]);

  // Push a layer (single-level or drill-down).
  const impersonate = useCallback(
    (target: ImpersonationTarget, opts?: { enteredFrom?: string }): void => {
      // Who is ACTING at this push: the worn role during a drill-down (top of
      // stack), else the real login role — which is reliable here because it's
      // only overwritten once an impersonation-scoped GET_USER resolves, i.e.
      // after this push.
      const actor = resolveActor(state, userRole);
      if (enforce) {
        if (!isImpersonationAllowed(actor, target.kind, policy)) {
          logger.warn(`Impersonation denied: ${actor} → ${target.kind}`, {
            actor,
            targetKind: target.kind,
          });
          onPermissionDenied?.({ actor, target });
          return;
        }
      }

      // Where a full exit should return to. Defaults to the current path, but the
      // caller can override it — e.g. the SSO auto-entry runs on /login/callback,
      // which would loop back into impersonation on exit, so it passes a real
      // advisor home instead.
      const origin = opts?.enteredFrom ?? window.location.pathname;

      // Stamp the layer with the acting role so consumers (e.g. the banner's
      // access-mode label) can read it back instead of the mutable role store.
      // `reporter` is resolveActor's fail-closed sentinel for an unset/unknown
      // role store (with enforce off, e.g. the SSO auto-entry can push before
      // any role is stored) — don't record it as knowledge; leave the layer
      // unstamped so consumers use their fallback.
      const next = pushTarget(
        state,
        target,
        origin,
        actor === ROLE_CHOICES[RoleType.Reporter] ? undefined : actor,
      );
      // Starting a fresh session (stack was empty): remember where we launched from in a
      // slot that survives the eventual full exit, so a later browser Back into a stale
      // route returns the staff user here instead of the generic /landing.
      if (!state) writeEntryOrigin(origin);
      // Cancel in-flight queries BEFORE the headers flip, so a lower-stack request (e.g.
      // the advisor's) can't resolve under the newly-pushed top layer's headers.
      queryClient.cancelQueries();
      persist(next);
      theme.applyFor(target);
      armedRef.current = false; // disarm until the push lands inside routePrefix
      router.push(ROLE_REGISTRY[target.kind].entryRoute(target as never));
    },
    [
      state,
      userRole,
      policy,
      enforce,
      onPermissionDenied,
      queryClient,
      persist,
      theme,
      router,
    ],
  );

  // Pop one layer; on the last layer this fully exits and returns to `enteredFrom`.
  // `noNavigate` = the browser already moved (nav-exit guard).
  const exit = useCallback(
    (opts?: { noNavigate?: boolean }): void => {
      if (!state) return;
      queryClient.cancelQueries(); // no in-flight query resolves with stale headers

      const { next, newTop } = popTarget(state);

      if (!next || !newTop) {
        // Full exit.
        persist(null);
        // Defer the cache drop until the exit navigation commits and the impersonated
        // experience unmounts (handled by the pathname effect above) — clearing inline
        // here refetches still-mounted observers under cleared headers.
        pendingExitClearRef.current = true;
        theme.reset();
        if (!opts?.noNavigate) {
          armedRef.current = false;
          // replace (not push) so Back can't return to the now-context-less route.
          router.replace(state.enteredFrom);
        }
        return;
      }

      // Pop to the layer below.
      persist(next);
      theme.applyFor(newTop);
      if (!opts?.noNavigate) {
        armedRef.current = false;
        router.replace(ROLE_REGISTRY[newTop.kind].entryRoute(newTop as never));
      }
    },
    [state, router, queryClient, persist, theme],
  );

  // Sugar over `impersonate` for the advisor→client (HOH) flow.
  const enterClientImpersonation = useCallback(
    (
      familyId: number | string,
      clientId?: number | string | null,
      opts?: { enteredFrom?: string },
    ): void => {
      impersonate(
        {
          kind: ROLE_CHOICES[RoleType.HeadOfTheFamily],
          userId: Number(clientId) || 0,
          familyId: Number(familyId),
        },
        opts,
      );
    },
    [impersonate],
  );

  // Fill in the top target's `userId` once the client page resolves it (no nav/theme change).
  const setImpersonatedUserId = useCallback(
    (userId: number): void => {
      const next = refineTopUserId(state, userId);
      if (next === state) return;
      persist(next);
    },
    [state, persist],
  );

  const canImpersonate = useCallback(
    (kind: ImpersonationKind): boolean =>
      isImpersonationAllowed(resolveActor(state, userRole), kind, policy),
    [state, userRole, policy],
  );

  const view = useMemo(() => deriveImpersonationView(state), [state]);

  useNavExitGuard({ state, pathname, armedRef });

  // After exit, a browser Back into the (now context-less) impersonated route tree
  // would render a broken page; bounce staff users home instead. See the hook.
  const staleRouteBlocked = useStaleImpersonationRouteGuard({
    state,
    pathname,
    enabled: hydrated,
  });

  // Identity-driven context loader. While impersonating, the only legitimate place to be
  // is inside the active identity's route tree; until the committed URL lands there, the
  // experience we're leaving is still mounted, so render the loader (the dashboard layout
  // swaps in a spinner) to unmount it BEFORE its queries can re-fire under the new
  // headers. Self-clears the moment the destination route commits — for ANY role switch,
  // with no time cap that could re-expose the source mid-switch. Also true while the
  // stale-route guard is bouncing staff home (avoids painting the broken page on Back).
  const activePrefix = view.current
    ? ROLE_REGISTRY[view.current.kind].routePrefix(view.current as never)
    : null;
  const prefixMatches = (prefix: string | string[]): boolean =>
    Array.isArray(prefix)
      ? prefix.some((p) => pathname.startsWith(p))
      : pathname.startsWith(prefix);
  const switchingContext =
    activePrefix !== null && !prefixMatches(activePrefix);
  const isTransitioning = switchingContext || staleRouteBlocked;

  return (
    <ImpersonationContext.Provider
      value={{
        state,
        ...view,
        isTransitioning,
        impersonate,
        enterClientImpersonation,
        exit,
        setImpersonatedUserId,
        canImpersonate,
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
}
